import { Trans, useLingui } from '@lingui/react/macro';
import { Checkbox, FormControl, FormLabel, Grid2, Stack, Typography, useTheme } from '@mui/material';
import { useMemo, useState } from 'react';
import { Control, useFieldArray } from 'react-hook-form';
import { formatCurrency } from '../utils/formatCurrency';
import { Addon, ProgramCurrency } from './Addons';
import { CourseReservation } from './CourseReservation';

interface AddonRowProps {
  addon: Addon;
  checked?: boolean;
  onToggle: () => void;
}

const AddonRow = ({ addon, checked = false, onToggle }: AddonRowProps) => {
  const theme = useTheme();
  const costFormatted = useMemo(() => formatCurrency(addon.price, addon.currency), [addon.price, addon.currency]);

  return (
    <FormControl sx={{ width: '100%' }}>
      <FormLabel
        sx={{
          border: `1px solid ${theme.palette.secondary.light}`,
          '&:hover': {
            borderColor: theme.palette.primary.main,
          },
          borderRadius: 2,
          cursor: 'pointer',
          my: 1,
          p: 2,
        }}
      >
        <Stack direction="row" sx={{ width: '100%' }} justifyContent="space-between" alignItems="center">
          <Stack direction="column">
            <Typography sx={{ fontSize: '1.3em' }}>{addon.name}</Typography>
            <Typography sx={{ fontSize: '1.1em', color: theme.palette.secondary.dark }}>{addon.description}</Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={3}>
            <Typography>{costFormatted}</Typography>
            <Checkbox checked={checked} onClick={onToggle} />
          </Stack>
        </Stack>
      </FormLabel>
    </FormControl>
  );
};

interface ProgramProps {
  addons: Addon[];
  control: Control<CourseReservation>;
  programName: string;
  programDescription: string;
  programCost: number;
  programCurrency: ProgramCurrency;
}

export const Program = ({ addons, control, programName, programDescription, programCost, programCurrency }: ProgramProps) => {
  const { t } = useLingui();
  const [noAddonsState, setNoAddonsState] = useState<boolean>(false);
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'courseAddons',
  });
  const fieldMap = useMemo(() => {
    const map = new Map<string, number>();
    fields.forEach((field, index) => {
      map.set(field.value, index);
    });
    return map;
  }, [fields]);

  const resetAddons = () => {
    // if we're checking 'none' then reset all previously selected add-ons
    if (!noAddonsState) replace([]);
    setNoAddonsState(() => !noAddonsState);
  };

  const onAddonToggle = (id: string) => {
    // unselect 'none' if we're selecting an add-on
    if (noAddonsState) setNoAddonsState(false);

    if (fieldMap.has(id)) remove(fieldMap.get(id)!);
    else append({ id, value: id });
  };

  const showAddonsSection = false;
  return (
    <Grid2 container spacing={3} id="program">
      <Grid2 size={12}>
        <Typography variant="subtitle1">
          <Trans>Selected Program</Trans>
        </Typography>
        <Typography>
          <Trans>Displayed price is the program's standard rate. Your final amount will be confirmed in the contract.</Trans>
        </Typography>
      </Grid2>
      <Grid2 size={12}>
        <AddonRow
          onToggle={() => false}
          addon={{
            id: 'program',
            name: programName,
            description: programDescription,
            price: programCost,
            currency: programCurrency,
            subProductType: '',
            subProductNumber: '',
          }}
          checked
        />
      </Grid2>
      {showAddonsSection && (
        <>
          <Grid2 size={12}>
            <Typography variant="subtitle1">
              <Trans>Add-ons</Trans>
            </Typography>
            <Typography>
              <Trans>You can select multiple add-ons to enhance your program experience.</Trans>
            </Typography>
          </Grid2>
          <Grid2 size={12}>
            <AddonRow
              key="none"
              onToggle={resetAddons}
              addon={{
                id: 'none',
                name: t`No Add-ons`,
                description: t`Continue without any additional services`,
                price: 0,
                currency: 'USD',
                subProductType: '',
                subProductNumber: '',
              }}
              checked={noAddonsState}
            />
            {addons.map((addon) => (
              <AddonRow key={addon.id} addon={addon} checked={fieldMap.has(addon.id)} onToggle={() => onAddonToggle(addon.id)} />
            ))}
          </Grid2>
        </>
      )}
    </Grid2>
  );
};
